import React, { useEffect, useMemo, useRef, useState } from 'react';

type GoogleWindow = Window & {
  google?: any;
  __payiqGoogleMapsPromise?: Promise<void>;
};

export interface GoogleAddressSelection {
  formattedAddress: string;
  addressLine: string;
  city: string;
  state: string;
  country: string;
  lat: number | null;
  lng: number | null;
  placeId: string;
}

interface Props {
  label?: string;
  value: string;
  placeholder?: string;
  error?: string | null;
  hint?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onSelect: (selection: GoogleAddressSelection) => void;
}

interface Prediction {
  description: string;
  place_id: string;
  structured_formatting?: { main_text?: string; secondary_text?: string };
}

const SCRIPT_ID = 'payiq-google-maps-places';

function loadGoogleMaps(): Promise<void> {
  const win = window as GoogleWindow;
  if (win.google?.maps?.places) return Promise.resolve();
  if (win.__payiqGoogleMapsPromise) return win.__payiqGoogleMapsPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error('Google Maps API key is not configured.'));
  }

  win.__payiqGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load.')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key,
    )}&libraries=places`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Maps failed to load.'));
    document.head.appendChild(script);
  });

  return win.__payiqGoogleMapsPromise;
}

function getComponent(
  components: Array<{ long_name: string; short_name: string; types: string[] }>,
  type: string,
) {
  return components.find((component) => component.types.includes(type));
}

function parsePlace(place: any): GoogleAddressSelection | null {
  const components = place.address_components ?? [];
  const streetNumber = getComponent(components, 'street_number')?.long_name;
  const route = getComponent(components, 'route')?.long_name;
  const city =
    getComponent(components, 'locality')?.long_name ??
    getComponent(components, 'administrative_area_level_2')?.long_name ??
    getComponent(components, 'sublocality')?.long_name ??
    '';
  const state = getComponent(components, 'administrative_area_level_1')?.long_name ?? '';
  const country = getComponent(components, 'country')?.long_name ?? 'Nigeria';
  const formattedAddress = place.formatted_address ?? place.name ?? '';
  const addressLine = [streetNumber, route].filter(Boolean).join(' ') || formattedAddress;
  const location = place.geometry?.location;
  const lat = location?.lat ? Number(location.lat()) : null;
  const lng = location?.lng ? Number(location.lng()) : null;

  if (!formattedAddress || !place.place_id || !city || !state) return null;

  return {
    formattedAddress,
    addressLine,
    city,
    state,
    country,
    lat,
    lng,
    placeId: place.place_id,
  };
}

export const GoogleAddressAutocomplete: React.FC<Props> = ({
  label,
  value,
  placeholder,
  error,
  hint,
  disabled,
  onChange,
  onSelect,
}) => {
  const serviceRef = useRef<any>(null);
  const placesRef = useRef<any>(null);
  const tokenRef = useRef<any>(null);
  const userTypedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted) return;
        const win = window as GoogleWindow;
        serviceRef.current = new win.google.maps.places.AutocompleteService();
        placesRef.current = new win.google.maps.places.PlacesService(document.createElement('div'));
        tokenRef.current = new win.google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((err: Error) => {
        if (mounted) setLoadError(err.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || selecting || !userTypedRef.current || value.trim().length < 3) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    const timer = window.setTimeout(() => {
      serviceRef.current?.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'ng' },
          sessionToken: tokenRef.current,
        },
        (results: Prediction[] | null) => {
          setPredictions(results ?? []);
          setOpen((results ?? []).length > 0);
        },
      );
    }, 250);

    return () => window.clearTimeout(timer);
  }, [ready, selecting, value]);

  const helper = useMemo(() => {
    if (loadError) return loadError;
    if (!ready) return 'Loading Google address search...';
    return hint ?? 'Start typing and select the correct Google address.';
  }, [hint, loadError, ready]);

  function handleSelect(prediction: Prediction) {
    if (!placesRef.current) return;
    setSelecting(true);
    placesRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        sessionToken: tokenRef.current,
      },
      (place: any) => {
        const parsed = parsePlace(place);
        if (parsed) {
          userTypedRef.current = false;
          onChange(parsed.formattedAddress);
          onSelect(parsed);
        }
        tokenRef.current = new (window as GoogleWindow).google.maps.places.AutocompleteSessionToken();
        setPredictions([]);
        setOpen(false);
        setSelecting(false);
      },
    );
  }

  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontSize: 13,
        color: 'var(--text-secondary)',
        position: 'relative',
      }}
    >
      {label ? <span style={{ fontWeight: 500 }}>{label}</span> : null}
      <input
        value={value}
        disabled={disabled || !!loadError}
        placeholder={placeholder}
        onChange={(e) => {
          userTypedRef.current = true;
          setSelecting(false);
          onChange(e.target.value);
        }}
        onFocus={() => setOpen(userTypedRef.current && predictions.length > 0)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        style={{
          height: 42,
          padding: '0 14px',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
          fontSize: 14,
          outline: 'none',
          transition: 'border-color 120ms ease',
        }}
      />
      {open ? (
        <div
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 70,
            left: 0,
            right: 0,
            maxHeight: 220,
            overflowY: 'auto',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 16px 32px rgba(15, 23, 42, 0.16)',
          }}
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(prediction)}
              style={{
                width: '100%',
                border: 0,
                background: 'transparent',
                padding: '10px 12px',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {prediction.structured_formatting?.main_text ?? prediction.description}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {prediction.structured_formatting?.secondary_text ?? prediction.description}
              </div>
            </button>
          ))}
        </div>
      ) : null}
      {error ? (
        <span style={{ color: 'var(--error)', fontSize: 12 }}>{error}</span>
      ) : helper ? (
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{helper}</span>
      ) : null}
    </label>
  );
};
